import { useEffect, useRef, ReactNode } from "react";
import { X } from "lucide-react";
export function PageIntro({
  eyebrow,
  title,
  children,
  action,
}: {
  eyebrow?: string;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="portal-intro">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {children && <div className="intro-copy">{children}</div>}
      </div>
      {action}
    </header>
  );
}
export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    el?.showModal();
    return () => el?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="portal-dialog"
      aria-label={title}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog-head">
        <h2>{title}</h2>
        <button
          className="icon-btn"
          aria-label="Close dialog"
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>
      {children}
    </dialog>
  );
}
export function Empty({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="portal-empty">
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

/**
 * Placeholder shaped like the page that is loading, so the layout does not
 * jump when data lands. Announced once to screen readers via the label.
 */
export function PageSkeleton({
  title,
  variant = "stack",
  label = "Loading",
}: {
  title: string;
  variant?: "coach" | "settings" | "billing" | "grid" | "stack";
  label?: string;
}) {
  const panel = (lines: number, key: number, tall = false) => (
    <section key={key} className="portal-panel skel-panel" aria-hidden="true">
      <span className="skel skel-eyebrow" />
      <span className={`skel ${tall ? "skel-title-lg" : "skel-title"}`} />
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="skel skel-line" style={{ width: `${92 - i * 14}%` }} />
      ))}
    </section>
  );
  const body =
    variant === "grid" ? (
      <div className="skel-grid" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="portal-panel skel-card">
            <span className="skel skel-thumb" />
            <span className="skel skel-line" style={{ width: "80%" }} />
            <span className="skel skel-line" style={{ width: "55%" }} />
          </div>
        ))}
      </div>
    ) : variant === "coach" ? (
      [panel(1, 0), panel(2, 1, true), panel(3, 2)]
    ) : variant === "settings" ? (
      [panel(2, 0), panel(2, 1), panel(1, 2), panel(1, 3)]
    ) : variant === "billing" ? (
      [panel(3, 0, true), panel(4, 1)]
    ) : (
      [panel(2, 0), panel(2, 1)]
    );
  return (
    <>
      <PageIntro title={title} />
      <div className="portal-stack" role="status" aria-live="polite">
        <span className="sr-only">{label}…</span>
        {body}
      </div>
    </>
  );
}

/** Full-screen loader for the moments before any page can render (session check, lazy chunk). */
export function FullPageLoader({ label }: { label: string }) {
  return (
    <div className="full-loader" role="status" aria-live="polite">
      <span className="full-loader-ring" aria-hidden="true" />
      <p>{label}</p>
    </div>
  );
}
